import React from 'react';
import { PrimaryRecordResourceCategory } from '../../../primary-record-types/MedicalDataTypes';
import { getIntl } from '../../../utils/intl';
import Stack from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { AlertIcon } from '../../v2BaseComponents/Icon';
import { PRScrollView, useIsInScrollView } from '../Scroll';

const headerTitles: Record<EmptyStateVariant, string> = {
    notes: 'No Notes have been added.',
    files: 'No documents have been added',
    medication: 'No Medications have been added',
    condition: 'No Conditions have been added',
    allergy: 'No Allergies have been added',
    procedure: 'No Procedures have been added',
    visit: 'No Visits have been added',
    lab: 'No Lab Results have been added',
    imaging: 'No Imaging results have been added',
    vaccination: 'No Vaccinations have been added',
    'care-team': 'Your Care Team has not been added',
    profiles: 'No Medical Profiles have been created.',
    'shared-report': 'No Shared Reports have been added',
    'audit-logs': 'No Activity Results have been generated',
    'audit-log-filter': 'No Activity Results match your search',
    'audit-logs-profile-selection': 'No Medical Profiles have been selected',
    'organization-groups': 'No Groups have been added',
    'organization-client-notes': 'No Notes have been added',
    'organization-client-files': 'No Organization files for this client',
    'files-or-folders': 'No files or folders have been added',
    'invoice-line-items': 'No invoice line items have been added for this client',
    'empty-profile-search': 'Search across your profile',
    'empty-message-search': 'No search results found',
    'empty-message-list': 'No messages found',
    'empty-notifications': 'No profile messages',
    'empty-notification-search': 'No profile messages found',
};

const primarySubTexts: Record<EmptyStateVariant, string> = {
    notes: 'Getting Organized with Notes',
    files: 'Helpful Tip:',
    medication: 'Helpful Tip:',
    condition: 'Helpful Tip:',
    allergy: 'Helpful Tip:',
    procedure: 'Helpful Tip:',
    visit: 'Helpful Tip:',
    lab: 'Helpful Tip:',
    imaging: 'Helpful Tip:',
    vaccination: 'Helpful Tip:',
    'care-team': 'Helpful Tip:',
    profiles: 'Helpful Tip:',
    'shared-report': 'Helpful Tip:',
    'audit-logs': 'Helpful Tip:',
    'audit-logs-profile-selection': 'Helpful Tip:',
    'audit-log-filter': 'Helpful Tip',
    'organization-groups': 'Helpful Tip:',
    'organization-client-notes': 'Helpful Tip:',
    'organization-client-files': 'Helpful Tip:',
    'files-or-folders': 'Helpful Tip:',
    'invoice-line-items': 'Helpful Tip:',
    'empty-profile-search': 'Helpful Tip:',
    'empty-message-search': 'Helpful Tip:',
    'empty-message-list': 'Helpful Tip:',
    'empty-notifications': 'Helpful Tip:',
    'empty-notification-search': 'Helpful Tip:',
};

const secondarySubTexts: Record<EmptyStateVariant, string> = {
    notes: 'Notes is a great place to make lists, upload files like lab reports, spreadsheets, images. We accept over 70 file types.',
    files: 'Add and organize audio files or documents so they can be summarized and easily searched by the Primary Record Chat Assistant.',
    medication:
        'Connect to Patient Portals, edit medications to inactive, or add new ones from home, keeping your list updated and ready to print for your Care Team.',
    condition:
        'Connect Patient Portals to gather all diagnosed Conditions in one place. Mark old Conditions as inactive or add new ones from other providers, keeping your list accurate and ready to print for your Care Team.',
    allergy:
        'Keep a list of allergies, intolerances, and items to avoid due to your condition or medications. Edit from Patient Portals or add to keep one place to quickly reference for safe care.',
    procedure:
        'Keep a history of procedures and surgeries in one place. Download from Patient Portals or add manually to easily share past history.',
    visit: 'Easily manage Healthcare Visits in one place. Download Visit dates or manually add them to record questions, directions, and care decisions. Your Notes are searchable and always at hand for easy recall.',
    lab: 'Easily manage lab results in one place. Connect to a Patient Portal to get started. Your lab values are searchable and always at hand for easy recall.',
    imaging:
        'Gather imaging records in one spot for easy searching and sharing. Download from Patient Portals or manually add x-rays, scans, MRIs, etc.',
    vaccination:
        'Connect to Patient Portals to download vaccination history or add vaccinations, so there is always an updated list to search and share.',
    'care-team':
        'Keep your providers and support contacts in one place. Add Notes and Link health information so you and your Collaborators always know who to contact with questions.',
    profiles:
        'Create a profile for yourself or someone else. Add health information, documents, and Notes to keep everything organized and easy to find.',
    'shared-report':
        'Shared reports allow you to share a report with anyone. Reports can be shared via a link or QR code. Anyone with the link or QR code can view the report.',
    'audit-logs':
        'Activity results show a history of who has accessed a profile. You can filter by profile and see what actions have been taken.',
    'audit-logs-profile-selection':
        'Select profiles to view activity results. You can filter by profile and see what actions have been taken.',
    'audit-log-filter': 'You can filter your results to a specific profile to see activity',
    'organization-groups':
        'Create groups to organize your clients and manage which agents have access to which clients.',
    'organization-client-notes':
        'Add Notes to your clients to keep track of important information. These Notes are only visible to users within your organization and not the client.',
    'organization-client-files':
        "This list contains all the files that have been uploaded by your organization for this client or files from the client added to your organization's folder in their profile.  If you have shared a file but do not see it, it may have been deleted by the client.",
    'files-or-folders':
        'Consider adding medical, legal, financial, and care plans.  All files (including audio) are summarized and easily searchable from the Chat Assistant.',
    'invoice-line-items':
        'Add line items to the invoice to bill for services.  You can add multiple line items to the invoice and adjust the quantity and price.',
    'empty-profile-search': 'Search for specific resources or general topics to find information quickly.',
    'empty-message-search': 'Try searching for a specific message within this profile.',
    'empty-message-list': 'There are no messages to display.  Messages within this profile would be displayed here.',
    'empty-notifications': 'There are no profile messages to display.',
    'empty-notification-search': 'Try altering your search to find your messages.',
};

const headerIntl = getIntl('empty-state-title', headerTitles);
const primarySubTextIntl = getIntl('empty-state-primary-subtext', primarySubTexts);
const secondarySubTextIntl = getIntl('empty-state-secondary-subtext', secondarySubTexts);

export type EmptyStateVariant =
    | 'files'
    | 'notes'
    | 'care-team'
    | 'profiles'
    | 'shared-report'
    | 'audit-logs'
    | 'audit-log-filter'
    | 'audit-logs-profile-selection'
    | PrimaryRecordResourceCategory
    | 'organization-groups'
    | 'organization-client-notes'
    | 'organization-client-files'
    | 'files-or-folders'
    | 'invoice-line-items'
    | 'empty-profile-search'
    | 'empty-message-search'
    | 'empty-message-list'
    | 'empty-notifications'
    | 'empty-notification-search';

export type EmptyStateHintOwnProps = {
    variant: EmptyStateVariant;
    renderSubContent?: () => React.ReactNode;
    renderNoKnownDrugAllergies?: React.ReactNode;
    hideDefaultHeader?: boolean;
};

export type EmptyStateHintProps = EmptyStateHintOwnProps;

const renderInContainer = (children: React.ReactNode) => (
    <Stack horizontal fullWidth justify="center">
        <Stack vertical style={{ maxWidth: 300, paddingTop: 40, paddingBottom: 40 }} align="center">
            {children}
        </Stack>
    </Stack>
);

const renderHeader = (content: string) => (
    <Stack horizontal justify="center" style={{ maxWidth: 240, marginBottom: 30 }}>
        <Text variant="empty-state-hint-header" alignText="center" wrapText>
            {content}
        </Text>
    </Stack>
);

const EmptyStateHint: React.FC<EmptyStateHintProps> = ({
    variant,
    renderSubContent,
    renderNoKnownDrugAllergies,
    hideDefaultHeader,
}) => {
    const header = headerIntl(variant);
    const primarySubText = primarySubTextIntl(variant);
    const secondarySubText = secondarySubTextIntl(variant);
    const isInScrollView = useIsInScrollView();

    const content = renderInContainer(
        <>
            {!hideDefaultHeader ? renderHeader(header) : null}
            {renderNoKnownDrugAllergies != null ? (
                <Stack horizontal justify="center" fullWidth style={{ marginBottom: 20 }}>
                    {renderNoKnownDrugAllergies}
                </Stack>
            ) : null}
            <AlertIcon size="lg" variant="info" name="Lightbulb" />
            <Stack
                vertical
                fullWidth
                align="center"
                style={{ marginTop: 20 }}
                space={8}
                testID={`empty-state-hint-${variant}`}
            >
                <Text variant="empty-state-hint-primary-sub-text" alignText="center" wrapText>
                    {primarySubText}
                </Text>
                <Text variant="body-1" alignText="center" wrapText>
                    {secondarySubText}
                </Text>
                {renderSubContent?.() || null}
            </Stack>
        </>,
    );

    if (isInScrollView) {
        return content;
    } else {
        return <PRScrollView style={{ flex: 1, width: '100%' }}>{content}</PRScrollView>;
    }
};

export default React.memo(EmptyStateHint);

export type LinkEmptyStateHintProps = {
    variant: EmptyStateVariant;
};

const linkHeaderTitles: Record<EmptyStateVariant, string> = {
    notes: 'No Notes have been added.',
    files: 'No Documents have been linked yet',
    medication: 'No Medications have been linked yet',
    condition: 'No Conditions have been linked.',
    allergy: 'No Allergies have been linked yet',
    procedure: 'No Procedures have been linked yet',
    visit: 'No Visits have been linked yet',
    lab: 'No Lab Results have been linked yet',
    imaging: 'No Imaging results have been linked yet',
    vaccination: 'No Vaccinations have been linked yet',
    'care-team': 'Your Care Team has no been linked.',
    profiles: 'No Medical Profiles have been linked yet',
    'shared-report': 'No shared reports have been linked yet',
    'audit-logs': 'No audit logs have been linked yet',
    'audit-logs-profile-selection': 'No Medical Profiles have been selected',
    'audit-log-filter': 'No audit logs match your filter',
    'organization-groups': 'No groups have been linked yet',
    'organization-client-notes': 'No notes have been linked yet',
    'organization-client-files': 'No organization files have been linked yet',
    'files-or-folders': 'No files or folders have been linked yet',
    'invoice-line-items': 'No invoice line items have been linked yet',
    'empty-profile-search': 'No search results found',
    'empty-message-search': 'No search results found',
    'empty-message-list': 'No messages found',
    'empty-notifications': 'No profile messages',
    'empty-notification-search': 'No profile messages found',
};

const linkedHeaderStateHeaderMessage = getIntl('linked-empty-state-header-message', linkHeaderTitles);

export const LinkEmptyStateHint: React.FC<LinkEmptyStateHintProps> = ({ variant }) => {
    return renderInContainer(renderHeader(linkedHeaderStateHeaderMessage(variant)));
};
